import { Link } from 'react-router-dom';
import { ArrowRight, Code2, Mail, MapPin, Phone } from 'lucide-react';
import { AUTHOR_INFO, COMPANY_INFO, FOOTER_LOGO_URL } from '../../data/company';
import { PRODUCT_CATEGORIES } from '../../data/products';
import { openCookiePreferences } from '../../lib/consent';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const phoneNumbers = COMPANY_INFO.contact.phone || [];
  const primaryEmail = COMPANY_INFO.contact.email?.[0];

  return (
    <footer className="tech-grid bg-[#0f172a] text-white" data-testid="footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-6">
            <Link to="/" className="inline-block">
              <div className="flex items-center gap-4">
                <img
                  src={FOOTER_LOGO_URL}
                  alt={COMPANY_INFO.name}
                  className="h-16 w-auto object-contain"
                />
              </div>
            </Link>
            <p className="text-sm leading-relaxed text-slate-300">
              {COMPANY_INFO.tagline}. Industrial weighbridge and weighing scale manufacturer serving India, Ghana, Africa, Dubai, and global markets with {COMPANY_INFO.stats.yearsExperience} years of excellence.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="tech-shimmer rounded-sm bg-[#0369A1] px-3 py-1 text-xs font-bold uppercase tracking-wider">
                {COMPANY_INFO.certification}
              </span>
              <span className="rounded-sm border border-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-200">
                Ghana, India + {COMPANY_INFO.stats.countries} Countries
              </span>
            </div>
          </div>

          <div>
            <h4 className="mb-6 text-lg font-bold uppercase tracking-wider">Products</h4>
            <ul className="space-y-3">
              {PRODUCT_CATEGORIES.map((category) => (
                <li key={category.id}>
                  <Link
                    to={`/products/${category.id}`}
                    className="group flex items-center gap-2 text-slate-300 transition-colors hover:text-sky-300"
                    data-testid={`footer-${category.id}`}
                  >
                    <ArrowRight className="h-4 w-4 -ml-6 opacity-0 transition-all group-hover:-ml-0 group-hover:opacity-100" />
                    {category.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/products" className="mt-4 inline-block font-semibold text-sky-300 hover:underline">
                  View All Products
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="mb-6 text-lg font-bold uppercase tracking-wider">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: 'About Us', href: '/about' },
                { name: 'Industries', href: '/industries' },
                { name: 'Support', href: '/support' },
                { name: 'Contact', href: '/contact' },
              ].map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="group flex items-center gap-2 text-slate-300 transition-colors hover:text-sky-300">
                    <ArrowRight className="h-4 w-4 -ml-6 opacity-0 transition-all group-hover:-ml-0 group-hover:opacity-100" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-6 text-lg font-bold uppercase tracking-wider">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="mt-1 h-5 w-5 flex-shrink-0 text-sky-300" />
                <span className="text-sm text-slate-300">
                  {COMPANY_INFO.address.office},<br />
                  {COMPANY_INFO.address.city} - {COMPANY_INFO.address.pincode},<br />
                  {COMPANY_INFO.address.state}, {COMPANY_INFO.address.country}
                </span>
              </li>
              {phoneNumbers.length > 0 && (
                <li className="flex items-center gap-3">
                  <Phone className="h-5 w-5 flex-shrink-0 text-sky-300" />
                  <div className="text-sm text-slate-300">
                    {phoneNumbers.map((phone) => (
                      <a key={phone} href={`tel:${phone}`} className="block hover:text-white">
                        {phone}
                      </a>
                    ))}
                  </div>
                </li>
              )}
              {primaryEmail && (
                <li className="flex items-center gap-3">
                  <Mail className="h-5 w-5 flex-shrink-0 text-sky-300" />
                  <a href={`mailto:${primaryEmail}`} className="text-sm text-slate-300 hover:text-white">
                    {primaryEmail}
                  </a>
                </li>
              )}
            </ul>

          </div>
        </div>
      </div>

      <div className="border-t border-slate-700">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <p className="text-sm text-slate-400">
              (c) {currentYear} {COMPANY_INFO.name}. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-slate-400 md:justify-end">
              <div className="group flex items-center gap-2 rounded-sm border border-white/10 bg-white/[0.03] px-3 py-2 text-slate-300 transition-colors hover:border-sky-300/40 hover:bg-sky-300/10 hover:text-white">
                <Code2 className="h-4 w-4 text-sky-300 transition-transform group-hover:scale-110" aria-hidden="true" />
                <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                  <span className="text-slate-400">Website engineered by</span>
                  <span className="font-semibold text-white">{AUTHOR_INFO.name}</span>
                </span>
              </div>
              <Link to="/privacy-policy" className="transition-colors hover:text-white">Privacy Policy</Link>
              <Link to="/cookie-policy" className="transition-colors hover:text-white">Cookie Policy</Link>
              <Link to="/terms-of-service" className="transition-colors hover:text-white">Terms of Service</Link>
              <button type="button" onClick={openCookiePreferences} className="transition-colors hover:text-white">
                Cookie Preferences
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
